import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { FieldErrors, UseFormRegister, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useConfirmDialog } from '~/components/DialogsProvider';
import { fetchEmployee } from '~/data/employee';
import { useEmployee } from '~/data/employee/useEmployee';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { AppDispatch } from '~/store';
import { isValidPhoneNumber } from '~/utils/helper';

import {
  EmpEmeContactType,
  deleteEmpEmeContactData,
  putEmeContactData,
} from './data';

export type IFormInput = {
  fEmpEmeEmail: string | undefined;
  fEmpEmeName: string;
  fEmpEmePhone: string | undefined;
};

export type useEmegencyContractOutput = {
  deleteContact: (e: React.MouseEvent<HTMLButtonElement>) => void;
  readMode: boolean;
  setReadMode: (data: boolean) => void;
  empData: EmpEmeContactType;
  onSubmit: () => void;
  register: UseFormRegister<IFormInput>;
  errors: FieldErrors<IFormInput>;
  handleFormReset: (totalReset: boolean) => void;
  isLoading: boolean;
};

export const useEmergencyContact = (): useEmegencyContractOutput => {
  const dispatch: AppDispatch = useDispatch();
  const confirm = useConfirmDialog();
  const [readMode, setReadMode] = useState(true);
  const {
    employee: { empEmeContact: empData, empBasicData },
    isLoading,
  } = useEmployee();
  const { t } = useTranslation();

  const validationSchema = React.useMemo(() => {
    return yup.object().shape<IFormInput>({
      fEmpEmeName: yup.string().required(t('laEmeContactError2')),
      fEmpEmeEmail: yup.string(),
      fEmpEmePhone: yup
        .string()
        .when('fEmpEmeEmail', {
          is: (fEmpEmeEmail: string) => !fEmpEmeEmail,
          then: yup.string().required(t('laEmeContactError1')),
        })
        .test('phoneNumberValidation', t('phoneInvalid'), isValidPhoneNumber),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<IFormInput>({
    resolver,
  });

  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit((data) => {
      const json: IFormInput = {
        fEmpEmeEmail: data.fEmpEmeEmail,
        fEmpEmeName: data.fEmpEmeName,
        fEmpEmePhone: data.fEmpEmePhone,
      };
      (async () => {
        try {
          await putEmeContactData(json, empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          setReadMode(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      })();
    });
  }, [dispatch, empBasicData.fEmpId, enqueueSnackbar, handleSubmit, t]);

  const onSubmit = () => {
    handleFormSubmit();
  };

  const handleFormReset = (totalReset: boolean) => {
    if (totalReset) {
      reset({ fEmpEmeEmail: '', fEmpEmeName: '', fEmpEmePhone: '' });
    } else {
      reset();
    }
    setReadMode(true);
  };

  const deleteContact = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    confirm(t('contactDeleteConfirmation'), async (confirmed) => {
      if (confirmed) {
        try {
          await deleteEmpEmeContactData(empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          handleFormReset(true);
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      }
    });
  };

  return {
    deleteContact,
    readMode,
    setReadMode,
    empData,
    errors,
    register,
    onSubmit,
    handleFormReset,
    isLoading,
  };
};
